import React, {
  ReactElement,
  useCallback,
  useEffect,
  useState,
  useRef,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from 'react';
import styled from 'styled-components/macro';
import cuid from 'cuid';
import {
  ClinicUpdateInput,
  useUpdateClinicSettingsClinicMutation,
  useGetClinicSettingsGeneralQuery,
  PermissionType,
  useGetSignedUploadUrlMutation,
} from 'shared/types/graphql';
import { ImageData } from 'pages/Settings/types/ImageData';
import useClinicUser from 'shared/hooks/useClinicUser';
import { GraphQLFetchPolicies } from 'shared/enums/GraphQLFetchPolicies';
import CropImageModal from '../../components/CropImageModal';
import PlaceHolderAvatar from 'assets/avatars/userPlaceholderAvatar.svg';
import { capitalize } from 'shared/utils';
import Tooltip from 'shared/components/Tooltip';
import {
  Button,
  Icon,
  Select,
  Text,
  TextInput,
  MenuItemProps,
  TextInputProps,
  Center,
  Divider,
  HStack,
  Image,
  Stack,
  useDisclosure,
  StackProps,
} from '@televet/kibble-ui';
import HasFeature from 'shared/components/HasFeature';
import { FeatureFlagName } from 'shared/enums/FeatureFlagName';
import { STATUSES, useCustomToast } from 'shared/hooks/useCustomToast';
import { RouteBasePaths, SettingsRoutes } from 'routes';
import { useHistory } from 'react-router-dom';
import EmployerIdentificationNumberModal from './EmployerIdentificationNumberModal';
import onPostalCodeCheck from 'shared/utils/zipcode';
import useDebounce from 'shared/hooks/useDebounce';
import { useTracking } from 'shared/providers/TrackingProvider';
import { SettingsGeneralInfoEvents } from 'shared/providers/TrackingProvider/EventNames';
import HasModule from 'shared/components/HasModule';
import { ModuleName } from '@televet/shared-types/productPackages';
import { useModularity } from 'shared/hooks/useModularity';
import VerifyPhoneNumberModal from '../../components/VerifyPhoneNumberModal';

enum ClinicUpdateFields {
  Name = 'name',
  WebsiteUrl = 'websiteUrl',
  ClinicEmail = 'clinicEmail',
  AddressLine1 = 'addressLine1',
  AddressLine2 = 'addressLine2',
  City = 'city',
  State = 'state',
  PostalCode = 'postalCode',
  Country = 'country',
}

export enum CountryCode {
  CA = 'CA',
  US = 'US',
}
interface IGeneralInfoComponentProps {
  title?: string;
  subtitle?: string;
  toolTip?: React.ReactNode;
  children?: React.ReactNode;
}

const GenInfoInputComponent = ({ ...rest }: TextInputProps): JSX.Element => {
  const { userHasPermission } = useClinicUser();
  const hasPermission = userHasPermission(PermissionType.WriteClinic);

  return <TextInput size="sm" isDisabled={!hasPermission} {...rest} />;
};

export const GeneralInfoComponent = ({
  title,
  subtitle,
  children,
  toolTip,
  ...rest
}: IGeneralInfoComponentProps & TextInputProps & StackProps): JSX.Element => {
  return (
    <Stack className="GeneralInfoComponent" spacing={0} margin="12px 0px" {...rest}>
      {title && (
        <Text as="span" size="md" fontWeight={600}>
          {title}
        </Text>
      )}
      {subtitle && (
        <HStack spacing={1}>
          <Text as="span" size="sm" fontWeight={600}>
            {subtitle}
          </Text>
          {toolTip}
        </HStack>
      )}
      {children}
    </Stack>
  );
};

const GeneralInfoSettings = forwardRef(
  (
    {
      hasPermission,
      isSettingsChangesValid,
      setHasUnsavedChange,
      setIsSettingsChangesValid,
    }: {
      hasPermission?: boolean;
      isSettingsChangesValid?: boolean;
      setHasUnsavedChange: React.Dispatch<React.SetStateAction<boolean>>;
      setIsSettingsChangesValid: React.Dispatch<React.SetStateAction<boolean>>;
    },
    ref,
  ): ReactElement => {
    // custom hooks
    const { currentClinicId } = useClinicUser();
    const { toastMessagePop } = useCustomToast();
    const history = useHistory();
    const { trackEvent } = useTracking();

    // modal hooks
    const {
      isOpen: isCropImageModalOpen,
      onOpen: onCropImageModalOpen,
      onClose: onCropImageModalClose,
    } = useDisclosure();
    const {
      isOpen: isVerifyPhoneNumberModalOpen,
      onOpen: onVerifyPhoneNumberModalOpen,
      onClose: onVerifyPhoneNumberModalClose,
    } = useDisclosure();
    // page constants
    const hiddenFileInputRef = useRef<HTMLInputElement | null>(null);

    // graphql hooks
    const [updateClinicSettings] = useUpdateClinicSettingsClinicMutation();
    const [getSignedUploadUrl] = useGetSignedUploadUrlMutation();
    const { data: currentClinic, refetch: getClinicSettingsData } = useGetClinicSettingsGeneralQuery({
      variables: { id: currentClinicId || '' },
      fetchPolicy: GraphQLFetchPolicies.CacheAndNetwork,
      skip: !currentClinicId,
    });

    // states
    const [clinicName, setClinicName] = useState<string>('');
    const [clinicPhoneNumber, setClinicPhoneNumber] = useState<string>('');
    const [clinicWebsiteUrl, setClinicWebsiteUrl] = useState<string>('');
    const [clinicEmail, setClinicEmail] = useState<string>('');
    const [logoUrl, setLogoUrl] = useState<string>('');
    const [imageData, setImageData] = useState<ImageData>();
    const [clinicGeneralInfoChanged, setClinicGeneralInfoChanged] = useState<ClinicUpdateFields[]>();
    const [addressFields, setAddressFields] = useState({
      addressLine1: '',
      addressLine2: '',
      city: '',
      state: '',
      postalCode: '',
      country: '',
    });

    const [postalCode] = useDebounce(addressFields.postalCode, 350);

    // handler function
    const clinicData = currentClinic?.findUniqueClinic;

    const { hasModule } = useModularity();
    const hasAppointmentsStandard = useMemo(() => hasModule(ModuleName.AppointmentsStandard), [hasModule]);

    const setInitialInfoData = useCallback(() => {
      if (clinicData) {
        setClinicPhoneNumber(
          clinicData.phone && ((clinicData.isCallerIdVerified && hasAppointmentsStandard) || !hasAppointmentsStandard)
            ? clinicData.phone
            : '',
        );
        setClinicName(clinicData.name || '');
        setClinicWebsiteUrl(clinicData.websiteUrl || '');
        setLogoUrl(clinicData.logo || '');
        setClinicEmail(clinicData.clinicEmail || '');

        const { addressLine1, addressLine2, city, state, postalCode, country } = clinicData;
        setAddressFields({
          addressLine1: addressLine1 || '',
          addressLine2: addressLine2 || '',
          city: city || '',
          state: state || '',
          postalCode: postalCode || '',
          country: country || 'US',
        });
      }
      setClinicGeneralInfoChanged([]);
      setHasUnsavedChange(false);
    }, [clinicData, hasAppointmentsStandard, setHasUnsavedChange]);

    const onUpdate = useCallback(
      async (data: ClinicUpdateInput) => {
        if (currentClinicId) {
          try {
            await updateClinicSettings({
              variables: { data, id: currentClinicId },
            });

            toastMessagePop();
            await getClinicSettingsData();
            setClinicGeneralInfoChanged([]);
          } catch (e) {
            console.error(e);
            toastMessagePop('Your clinic was unable to be updated. Please try again.', STATUSES.error);
          }
        }
      },
      [currentClinicId, updateClinicSettings, toastMessagePop, getClinicSettingsData],
    );

    const handleClinicLogoChange = useCallback(
      (event: React.ChangeEvent<HTMLInputElement>): void => {
        const files = event?.target?.files;

        if (files?.length) {
          const { name, size } = files[0];
          setImageData({ name, size: size / 1024 / 1024, file: files[0] });
          onCropImageModalOpen();
        }
      },
      [onCropImageModalOpen],
    );

    const handleUpdateImage = async (blob: Blob): Promise<void> => {
      if (imageData) {
        const { data } = await getSignedUploadUrl({
          variables: {
            data: {
              key: `ClinicLogo/${cuid()}_${imageData.file.name}`,
              contentType: imageData.file.type,
              isPublic: true,
            },
          },
        });
        if (!data?.getSignedUploadUrl.uploadUrl) {
          console.error('Unable to upload Clinic Image');
          toastMessagePop('Unable to upload new image', STATUSES.error);
          return;
        }
        await fetch(data.getSignedUploadUrl.uploadUrl, {
          method: 'Put',
          body: blob,
          headers: { 'Content-Type': imageData.file.type },
        });
        onUpdate({ logo: data.getSignedUploadUrl.finalUrl });
      }
    };

    const dataBuilder = (fieldName: ClinicUpdateFields): Record<string, string> | undefined => {
      switch (fieldName) {
        case ClinicUpdateFields.Country:
          return { [fieldName]: addressFields.country.toUpperCase().trim() };
        case ClinicUpdateFields.WebsiteUrl:
          return {
            [fieldName]: `https://${clinicWebsiteUrl
              .replace('http://', '')
              .replace('https://', '')
              .replace('www.', '')
              .trim()}`,
          };
        case ClinicUpdateFields.Name:
          return { [fieldName]: clinicName.trim() };
        case ClinicUpdateFields.ClinicEmail:
          return { [fieldName]: clinicEmail.trim() };
        case ClinicUpdateFields.AddressLine1:
          return { [fieldName]: addressFields.addressLine1.trim() };
        case ClinicUpdateFields.AddressLine2:
          return { [fieldName]: addressFields.addressLine2.trim() };
        case ClinicUpdateFields.City:
          return { [fieldName]: addressFields.city.trim() };
        case ClinicUpdateFields.PostalCode:
          return { [fieldName]: addressFields.postalCode.trim() };
        case ClinicUpdateFields.State:
          return { [fieldName]: addressFields.state.trim() };
        default:
          break;
      }
    };

    const onAddressInfoChanged = useCallback(
      (postalCode: string, countryCode: string): void => {
        const response = onPostalCodeCheck(postalCode, countryCode);
        setIsSettingsChangesValid(response);
      },
      [setIsSettingsChangesValid],
    );

    const onAddressFieldChanged = (field: ClinicUpdateFields, value: string): void => {
      setAddressFields((prevState) => ({ ...prevState, [field]: capitalize(value) }));
    };

    const onGeneralInfoChange = (field: ClinicUpdateFields, value: string): void => {
      const infoChanged = [...(clinicGeneralInfoChanged || [])];

      if (clinicData && clinicData[field] !== value) {
        setHasUnsavedChange(true);
        if (infoChanged.includes(field) === false) {
          infoChanged.push(field);
        }
        setClinicGeneralInfoChanged(infoChanged);
      } else if (clinicData && clinicData[field] === value) {
        const newState = infoChanged?.filter((clinicHourUpdateField) => clinicHourUpdateField !== field);
        setClinicGeneralInfoChanged(() => {
          return newState || [];
        });
        if (newState?.length === 0) {
          setHasUnsavedChange(false);
        }
      }
    };

    const onSaveGeneralInfoChange = (): void => {
      if (!!clinicGeneralInfoChanged && clinicGeneralInfoChanged.length > 0) {
        let generalInfoUpdate = {};
        clinicGeneralInfoChanged.map((val) => {
          generalInfoUpdate = { ...generalInfoUpdate, ...dataBuilder(val) };
        });
        onUpdate(generalInfoUpdate);
      }
    };

    const countryCodeCheck = useMemo((): boolean => {
      return addressFields.country === CountryCode.CA || addressFields.country === '' || addressFields.country === ' ';
    }, [addressFields.country]);

    useEffect(() => {
      onAddressInfoChanged(postalCode, addressFields.country);
    }, [addressFields.country, onAddressInfoChanged, postalCode]);

    // forwarded function
    useImperativeHandle(ref, () => ({
      onLeavingCurrentPage(): void {
        setClinicGeneralInfoChanged([]);
      },
      onClickedSaveButton(): void {
        onSaveGeneralInfoChange();
      },
      onClickedCancelButton(): void {
        setInitialInfoData();
      },
    }));

    useEffect(() => {
      setInitialInfoData();
    }, [setInitialInfoData]);

    return (
      <>
        <VerifyPhoneNumberModal isOpen={isVerifyPhoneNumberModalOpen} onClose={onVerifyPhoneNumberModalClose} />
        {imageData && (
          <CropImageModal
            fileUrl={URL.createObjectURL(imageData.file)}
            onApplyCrop={handleUpdateImage}
            isOpen={isCropImageModalOpen}
            onClose={onCropImageModalClose}
            setImageData={(): void => setImageData(undefined)}
          />
        )}
        <GeneralInfoComponent title="Clinic Logo">
          <HStack spacing={3.5}>
            <Image
              mt="10px"
              boxSize="69px"
              borderRadius="6px"
              objectFit="contain"
              src={logoUrl || PlaceHolderAvatar}
              alt="Clinic logo"
            />
            <HiddenFileInput
              onChange={handleClinicLogoChange}
              onClick={(e): void => {
                e.currentTarget.value = '';
              }}
              ref={hiddenFileInputRef}
              type="file"
              accept="image/*"
            />
            {hasPermission && (
              <Button
                variant="secondary"
                size="xs"
                iconPosition="right"
                p="0px 14px"
                onClick={(): void => {
                  if (hiddenFileInputRef && hiddenFileInputRef.current) {
                    hiddenFileInputRef.current.click();
                    trackEvent(SettingsGeneralInfoEvents.EditClinicLogo);
                  }
                }}
                lineHeight="0"
                _focus={{ boxShadow: 'none' }}
              >
                Choose File
              </Button>
            )}
          </HStack>
          <Text size="sm" variant="placeholder">
            Must be a .jpg or .png file smaller than 5 MB and at least 400 by 400.
          </Text>
        </GeneralInfoComponent>
        <Divider mt={5} mb={5} />
        <GeneralInfoComponent title="Clinic Display Name">
          <GenInfoInputComponent
            w={478}
            placeholder="Otto Animal Clinic"
            value={clinicName}
            onChange={(e): void => {
              setClinicName(e.target.value), onGeneralInfoChange(ClinicUpdateFields.Name, e.target.value);
            }}
          />
        </GeneralInfoComponent>

        <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
          <Divider mt={5} mb={5} />
          <GeneralInfoComponent title="Clinic Hours">
            <Text
              cursor="pointer"
              width="fit-content"
              size="xs"
              fontWeight="bold"
              textDecoration="underline"
              variant="interactive"
              onClick={(): void => history.push(`${RouteBasePaths.Settings}${SettingsRoutes.ClinicHours}`)}
            >
              View Clinic Hours
            </Text>
          </GeneralInfoComponent>
        </HasModule>
        <Divider mt={5} mb={5} />
        <GeneralInfoComponent title="Clinic Information" />
        <GeneralInfoComponent subtitle="Street Address Line 1">
          <GenInfoInputComponent
            width={478}
            placeholder="1400 Mulberry Lane"
            value={addressFields.addressLine1}
            onChange={(e): void => {
              onAddressFieldChanged(ClinicUpdateFields.AddressLine1, e.target.value);
              onGeneralInfoChange(ClinicUpdateFields.AddressLine1, e.target.value);
            }}
          />
        </GeneralInfoComponent>

        <GeneralInfoComponent subtitle="Street Address Line 2">
          <GenInfoInputComponent
            width={478}
            placeholder="Suite 1738 #5"
            value={addressFields.addressLine2}
            onChange={(e): void => {
              onAddressFieldChanged(ClinicUpdateFields.AddressLine2, e.target.value);
              onGeneralInfoChange(ClinicUpdateFields.AddressLine2, e.target.value);
            }}
          />
        </GeneralInfoComponent>
        <Row>
          <GeneralInfoComponent subtitle="City">
            <GenInfoInputComponent
              placeholder="Austin"
              value={addressFields.city}
              onChange={(e): void => {
                onAddressFieldChanged(ClinicUpdateFields.City, e.target.value);
                onGeneralInfoChange(ClinicUpdateFields.City, e.target.value);
              }}
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent subtitle="State">
            <GenInfoInputComponent
              placeholder="TX"
              value={addressFields.state}
              onChange={(e): void => {
                onAddressFieldChanged(ClinicUpdateFields.State, e.target.value);
                onGeneralInfoChange(ClinicUpdateFields.State, e.target.value);
              }}
              maxLength={2}
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent subtitle="Country Code">
            <Select
              w={200}
              size="sm"
              listProps={{
                onSelect: (selectedItem: MenuItemProps): void => {
                  onAddressInfoChanged(addressFields.postalCode, selectedItem.value || ' ');
                  onAddressFieldChanged(ClinicUpdateFields.Country, selectedItem.value || ' ');
                  onGeneralInfoChange(ClinicUpdateFields.Country, selectedItem.value || ' ');
                },
              }}
              placeholder="US"
              isDisabled={!hasPermission}
              options={[
                {
                  isSelected: false,
                  label: CountryCode.US,
                  value: CountryCode.US,
                },
                {
                  isSelected: false,
                  label: CountryCode.CA,
                  value: CountryCode.CA,
                },
              ]}
              value={addressFields.country}
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent subtitle="Postal Code">
            <GenInfoInputComponent
              placeholder={addressFields.country === CountryCode.US ? '12345' : 'A1A 1A1'}
              value={addressFields.postalCode}
              maxLength={countryCodeCheck ? 7 : 5}
              onChange={(e): void => {
                onAddressFieldChanged(ClinicUpdateFields.PostalCode, e.target.value);
                onGeneralInfoChange(ClinicUpdateFields.PostalCode, e.target.value);
              }}
              isInvalid={!isSettingsChangesValid}
              formControlStyle={{ maxW: '200px' }}
              errorText="Postal code doesn't match country code format"
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent subtitle="Clinic Phone Number">
            <HStack>
              <Text size="md" variant="placeholder">
                {clinicPhoneNumber || '###-###-####'}
              </Text>
              <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
                {hasPermission && (
                  <Center
                    onClick={(): void => {
                      onVerifyPhoneNumberModalOpen();
                    }}
                  >
                    <Icon name="pen" size="md" />
                  </Center>
                )}
              </HasModule>
            </HStack>
            <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
              <Text
                cursor={'pointer'}
                width="fit-content"
                size="xs"
                fontWeight="semibold"
                textDecoration="underline"
                variant="interactive"
                onClick={(): void => history.push(`${RouteBasePaths.Settings}${SettingsRoutes.ClinicPhone}`)}
              >
                Text Messaging Settings
              </Text>
            </HasModule>
          </GeneralInfoComponent>
          <GeneralInfoComponent subtitle="Clinic Website URL">
            <GenInfoInputComponent
              placeholder="https://otto.vet"
              value={clinicWebsiteUrl}
              onChange={(e): void => {
                setClinicWebsiteUrl(e.target.value), onGeneralInfoChange(ClinicUpdateFields.WebsiteUrl, e.target.value);
              }}
            />
          </GeneralInfoComponent>
          <GeneralInfoComponent>
            <HStack align="center" mb={2} spacing={1}>
              <Text size="sm" fontWeight="bold">
                Email
              </Text>
              <HasFeature name={FeatureFlagName.CarePlans}>
                <Tooltip content={'Sets the reply address for clinic emails.'}>
                  <Icon name="infoCircle" size="xs" />
                </Tooltip>
              </HasFeature>
            </HStack>
            <GenInfoInputComponent
              placeholder={clinicEmail || 'clinic@email.com'}
              value={clinicEmail}
              onChange={(e): void => {
                setClinicEmail(e.target.value), onGeneralInfoChange(ClinicUpdateFields.ClinicEmail, e.target.value);
              }}
            />
          </GeneralInfoComponent>
          <HasModule moduleToCheck={ModuleName.AppointmentsStandard}>
            <GeneralInfoComponent>
              <EmployerIdentificationNumberModal />
            </GeneralInfoComponent>
          </HasModule>
        </Row>
      </>
    );
  },
);

export default GeneralInfoSettings;

const HiddenFileInput = styled.input`
  display: none;
`;

const Row = styled.div`
  max-width: 478px;
  display: flex;
  flex-wrap: wrap;
  justify-content: space-between;
  input {
    width: 200px;
  }
`;
